import { useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus, Play, Pause, Trash2, Edit3, BarChart3, TrendingUp, TrendingDown,
  Eye, CheckCircle2, XCircle, AlertTriangle, Clock, Zap, ArrowRight,
  ChevronDown, ChevronUp, Target, Users, Percent, Activity,
  MessageSquare, Globe, GitBranch, X, Save, Filter, Archive,
  Bell, Download, FileText, History, Search, MoreHorizontal,
  PlayCircle, StopCircle, RotateCcw,
} from 'lucide-react';
import {
  getEnrichedTests, createTest, updateTest, deleteTest,
  startTest, pauseTest, resumeTest, completeTest, archiveTest,
  testTypes, successMetrics, splitRatioOptions, statusOptions,
  testTemplates, exportToCSV, exportToJSON,
} from '../data/abTests';
import ExperimentForm from './ExperimentForm';
import {
  VariantComparisonTable, TimeSeriesChart, ConversionRateChart,
  WinnerDeclaration, ExportResults,
} from './ResultsChart';
import { StatisticalSummary } from './StatisticalSummary';

// ─── Helpers ───
const statusConfig = {
  draft: { label: 'Draft', color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', dot: 'bg-zinc-500' },
  running: { label: 'Running', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', dot: 'bg-emerald-500' },
  paused: { label: 'Paused', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', dot: 'bg-amber-500' },
  completed: { label: 'Completed', color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30', dot: 'bg-sky-500' },
  archived: { label: 'Archived', color: 'text-zinc-500', bg: 'bg-zinc-700/10', border: 'border-zinc-700/30', dot: 'bg-zinc-600' },
};

const typeIcons = { page: Globe, message: MessageSquare, flow: GitBranch };
const typeLabels = { page: 'Page', message: 'Message', flow: 'Flow' };

const formatNumber = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const formatDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Mini Bar Chart ───
function MiniBarChart({ dataA, dataB, labelA, labelB }) {
  const allValues = [...dataA.map(d => d.conversions), ...dataB.map(d => d.conversions)];
  const max = Math.max(...allValues, 1);

  return (
    <div className='space-y-2'>
      <div className='flex items-end gap-[2px] h-16'>
        {dataA.map((d, i) => {
          const hA = (d.conversions / max) * 100;
          const hB = dataB[i] ? (dataB[i].conversions / max) * 100 : 0;
          return (
            <div key={i} className='flex-1 flex gap-[1px] items-end'>
              <div
                className='flex-1 bg-amber-500/60 hover:bg-amber-500 transition-colors rounded-t-sm min-h-[2px]'
                style={{ height: `${hA}%` }}
                title={`${labelA}: ${d.conversions}`}
              />
              <div
                className='flex-1 bg-sky-500/60 hover:bg-sky-500 transition-colors rounded-t-sm min-h-[2px]'
                style={{ height: `${hB}%` }}
                title={`${labelB}: ${dataB[i]?.conversions || 0}`}
              />
            </div>
          );
        })}
      </div>
      <div className='flex items-center gap-4'>
        <div className='flex items-center gap-1.5'>
          <div className='w-2 h-2 bg-amber-500 rounded-sm' />
          <span className='font-mono text-[9px] text-zinc-500 uppercase'>{labelA}</span>
        </div>
        <div className='flex items-center gap-1.5'>
          <div className='w-2 h-2 bg-sky-500 rounded-sm' />
          <span className='font-mono text-[9px] text-zinc-500 uppercase'>{labelB}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Confidence Badge ───
function ConfidenceBadge({ value }) {
  const color = value >= 95 ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
    : value >= 80 ? 'text-amber-400 border-amber-500/30 bg-amber-500/10'
    : 'text-zinc-400 border-zinc-700 bg-zinc-800/50';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[10px] uppercase tracking-wider ${color}`}>
      <Target className='w-3 h-3' />
      {value}%
    </span>
  );
}

// ─── Winner Badge ───
function WinnerBadge({ winner, rateA, rateB }) {
  if (!winner) return null;
  const isB = winner === 'b';
  const Icon = TrendingUp;
  const color = isB ? 'text-emerald-400' : 'text-amber-400';
  return (
    <div className={`flex items-center gap-1.5 ${color}`}>
      <Icon className='w-3.5 h-3.5' />
      <span className='font-mono text-[10px] uppercase tracking-wider font-bold'>
        Variant {isB ? 'B' : 'A'} wins ({isB ? rateB : rateA}%)
      </span>
    </div>
  );
}

// ─── Test Detail Panel ───
function TestDetail({ test, onBack, onStatusChange, onExport }) {
  const { t } = useTranslation();
  const sc = statusConfig[test.status];
  const TypeIcon = typeIcons[test.type];
  const { stats } = test;

  const handleExportCSV = () => {
    const csv = exportToCSV(test);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${test.id}-results.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const json = exportToJSON(test);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${test.id}-results.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    import('jspdf').then(({ jsPDF }) => {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(`A/B Test Results: ${test.name}`, 20, 20);
      doc.setFontSize(10);
      doc.text(`Status: ${test.status}`, 20, 35);
      doc.text(`Variant A (${test.variantA.name}): ${stats.rateA}% conv. rate`, 20, 50);
      doc.text(`Variant B (${test.variantB.name}): ${stats.rateB}% conv. rate`, 20, 60);
      doc.text(`Confidence: ${stats.confidence}%`, 20, 70);
      doc.text(`P-Value: ${stats.pValue}`, 20, 80);
      doc.text(`Lift: ${stats.lift > 0 ? '+' : ''}${stats.lift}%`, 20, 90);
      doc.text(`Winner: ${stats.winner ? `Variant ${stats.winner.toUpperCase()}` : 'None yet'}`, 20, 100);
      doc.save(`${test.id}-results.pdf`);
    });
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-start justify-between gap-4'>
        <div>
          <button
            onClick={onBack}
            className='flex items-center gap-1 text-zinc-500 hover:text-amber-500 font-mono text-[10px] uppercase tracking-wider mb-3 transition-colors'
          >
            ← {t('ab_testing.back_to_all')}
          </button>
          <h2 className='font-display text-2xl uppercase'>{test.name}</h2>
          <p className='text-zinc-500 text-sm mt-1'>{test.description}</p>
        </div>
        <div className='flex items-center gap-2'>
          <ExportResults
            test={test}
            onExportCSV={handleExportCSV}
            onExportJSON={handleExportJSON}
            onExportPDF={handleExportPDF}
          />
          <div className={`flex items-center gap-2 px-3 py-1.5 border ${sc.border} ${sc.bg}`}>
            <div className={`w-2 h-2 rounded-full ${sc.dot} ${test.status === 'running' ? 'animate-pulse' : ''}`} />
            <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${sc.color}`}>{sc.label}</span>
          </div>
        </div>
      </div>

      {/* Status Actions */}
      <div className='flex items-center gap-2 flex-wrap'>
        {test.status === 'draft' && (
          <button
            onClick={() => onStatusChange(test.id, 'start')}
            className='flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-500/20 transition-colors'
          >
            <PlayCircle className='w-3.5 h-3.5' /> {t('ab_testing.start_test')}
          </button>
        )}
        {test.status === 'running' && (
          <button
            onClick={() => onStatusChange(test.id, 'pause')}
            className='flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono text-[10px] uppercase tracking-wider hover:bg-amber-500/20 transition-colors'
          >
            <Pause className='w-3.5 h-3.5' /> {t('ab_testing.pause_test')}
          </button>
        )}
        {test.status === 'paused' && (
          <button
            onClick={() => onStatusChange(test.id, 'resume')}
            className='flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-500/20 transition-colors'
          >
            <PlayCircle className='w-3.5 h-3.5' /> {t('ab_testing.resume_test')}
          </button>
        )}
        {(test.status === 'running' || test.status === 'paused') && (
          <button
            onClick={() => onStatusChange(test.id, 'complete')}
            className='flex items-center gap-1.5 px-3 py-1.5 bg-sky-500/10 border border-sky-500/30 text-sky-400 font-mono text-[10px] uppercase tracking-wider hover:bg-sky-500/20 transition-colors'
          >
            <StopCircle className='w-3.5 h-3.5' /> {t('ab_testing.complete_test')}
          </button>
        )}
        {test.status === 'completed' && (
          <button
            onClick={() => onStatusChange(test.id, 'archive')}
            className='flex items-center gap-1.5 px-3 py-1.5 bg-zinc-700/10 border border-zinc-700/30 text-zinc-400 font-mono text-[10px] uppercase tracking-wider hover:bg-zinc-700/20 transition-colors'
          >
            <Archive className='w-3.5 h-3.5' /> {t('ab_testing.archive_test')}
          </button>
        )}
      </div>

      {/* Quick Stats */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.total_impressions')}</div>
          <div className='font-display text-2xl'>{formatNumber(stats.totalImpressions)}</div>
        </div>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.total_conversions')}</div>
          <div className='font-display text-2xl'>{formatNumber(stats.totalConversions)}</div>
        </div>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.confidence')}</div>
          <div className='mt-1'><ConfidenceBadge value={stats.confidence} /></div>
        </div>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.lift')}</div>
          <div className={`font-display text-2xl ${stats.lift > 0 ? 'text-emerald-400' : stats.lift < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
            {stats.lift > 0 ? '+' : ''}{stats.lift}%
          </div>
        </div>
      </div>

      {/* Winner Declaration */}
      <WinnerDeclaration test={test} />

      {/* Variant Comparison Table */}
      <VariantComparisonTable test={test} />

      {/* Time Series Chart */}
      <TimeSeriesChart test={test} />

      {/* Statistical Summary */}
      <StatisticalSummary test={test} />

      {/* Test Info */}
      <div className='border border-zinc-800 bg-zinc-950/30 p-5'>
        <h3 className='font-display text-sm uppercase mb-3'>{t('ab_testing.test_info')}</h3>
        <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
          <div>
            <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.type')}</span>
            <div className='flex items-center gap-1.5 mt-1'>
              <TypeIcon className='w-3.5 h-3.5 text-zinc-400' />
              <span className='font-mono text-xs text-zinc-300'>{typeLabels[test.type]}</span>
            </div>
          </div>
          <div>
            <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.split')}</span>
            <div className='font-mono text-xs text-zinc-300 mt-1'>{test.splitRatio.a}/{test.splitRatio.b}</div>
          </div>
          <div>
            <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.primary_metric')}</span>
            <div className='font-mono text-xs text-zinc-300 mt-1'>{successMetrics.find(m => m.value === test.successMetric)?.label}</div>
          </div>
          {test.secondaryMetric && (
            <div>
              <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.secondary_metric')}</span>
              <div className='font-mono text-xs text-zinc-300 mt-1'>{successMetrics.find(m => m.value === test.secondaryMetric)?.label}</div>
            </div>
          )}
          <div>
            <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.audience')}</span>
            <div className='font-mono text-xs text-zinc-300 mt-1'>{test.audiencePercentage}%</div>
          </div>
          <div>
            <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.duration')}</span>
            <div className='font-mono text-xs text-zinc-300 mt-1'>{test.estimatedDurationDays} {t('ab_testing.days')}</div>
          </div>
          {test.startedAt && (
            <div>
              <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.started')}</span>
              <div className='font-mono text-xs text-zinc-300 mt-1'>{formatDate(test.startedAt)}</div>
            </div>
          )}
          {test.completedAt && (
            <div>
              <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.completed')}</span>
              <div className='font-mono text-xs text-zinc-300 mt-1'>{formatDate(test.completedAt)}</div>
            </div>
          )}
          {test.hypothesis && (
            <div className='col-span-2 md:col-span-3'>
              <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>{t('ab_testing.hypothesis')}</span>
              <div className='font-mono text-xs text-zinc-300 mt-1'>{test.hypothesis}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Test Card ───
function TestCard({ test, onView, onEdit, onStatusChange, onDelete }) {
  const { t } = useTranslation();
  const sc = statusConfig[test.status];
  const TypeIcon = typeIcons[test.type];
  const { stats } = test;

  return (
    <div className='border border-zinc-800 bg-zinc-950/30 hover:border-zinc-700 transition-colors'>
      <div className='p-5'>
        <div className='flex items-start justify-between gap-3 mb-3'>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2 mb-1'>
              <TypeIcon className='w-4 h-4 text-zinc-500 shrink-0' />
              <h3 className='font-display text-base uppercase truncate'>{test.name}</h3>
            </div>
            <p className='text-zinc-500 text-xs truncate'>{test.description}</p>
          </div>
          <div className={`flex items-center gap-1.5 px-2 py-1 border shrink-0 ${sc.border} ${sc.bg}`}>
            <div className={`w-1.5 h-1.5 rounded-full ${sc.dot} ${test.status === 'running' ? 'animate-pulse' : ''}`} />
            <span className={`font-mono text-[9px] uppercase tracking-wider ${sc.color}`}>{sc.label}</span>
          </div>
        </div>

        {/* Stats Row */}
        <div className='grid grid-cols-4 gap-3 mb-4'>
          <div>
            <div className='font-mono text-[9px] uppercase tracking-wider text-zinc-600'>{t('ab_testing.impressions')}</div>
            <div className='font-mono text-xs text-zinc-300'>{formatNumber(stats.totalImpressions)}</div>
          </div>
          <div>
            <div className='font-mono text-[9px] uppercase tracking-wider text-zinc-600'>{t('ab_testing.conv_a')}</div>
            <div className='font-mono text-xs text-amber-400'>{stats.rateA}%</div>
          </div>
          <div>
            <div className='font-mono text-[9px] uppercase tracking-wider text-zinc-600'>{t('ab_testing.conv_b')}</div>
            <div className='font-mono text-xs text-sky-400'>{stats.rateB}%</div>
          </div>
          <div>
            <div className='font-mono text-[9px] uppercase tracking-wider text-zinc-600'>{t('ab_testing.confidence')}</div>
            <ConfidenceBadge value={stats.confidence} />
          </div>
        </div>

        {/* Winner / Lift */}
        <div className='flex items-center justify-between mb-4'>
          {stats.winner ? (
            <WinnerBadge winner={stats.winner} rateA={stats.rateA} rateB={stats.rateB} />
          ) : (
            <span className='font-mono text-[10px] text-zinc-600'>{t('ab_testing.no_winner_yet')}</span>
          )}
          <div className={`font-mono text-xs ${stats.lift > 0 ? 'text-emerald-400' : stats.lift < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
            {stats.lift > 0 ? '+' : ''}{stats.lift}% {t('ab_testing.lift')}
          </div>
        </div>

        {/* Mini Chart */}
        {test.timeSeriesA.length > 0 && (
          <div className='mb-4'>
            <MiniBarChart
              dataA={test.timeSeriesA}
              dataB={test.timeSeriesB}
              labelA='A'
              labelB='B'
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className='flex items-center border-t border-zinc-800 divide-x divide-zinc-800'>
        <button
          onClick={() => onView(test)}
          className='flex-1 flex items-center justify-center gap-1.5 py-2.5 text-zinc-400 hover:text-amber-500 font-mono text-[10px] uppercase tracking-wider transition-colors'
        >
          <Eye className='w-3.5 h-3.5' /> {t('ab_testing.view')}
        </button>
        <button
          onClick={() => onEdit(test)}
          className='flex-1 flex items-center justify-center gap-1.5 py-2.5 text-zinc-400 hover:text-amber-500 font-mono text-[10px] uppercase tracking-wider transition-colors'
        >
          <Edit3 className='w-3.5 h-3.5' /> {t('ab_testing.edit')}
        </button>
        {test.status === 'running' ? (
          <button
            onClick={() => onStatusChange(test.id, 'pause')}
            className='flex-1 flex items-center justify-center gap-1.5 py-2.5 text-zinc-400 hover:text-amber-500 font-mono text-[10px] uppercase tracking-wider transition-colors'
          >
            <Pause className='w-3.5 h-3.5' /> {t('ab_testing.pause')}
          </button>
        ) : test.status === 'paused' || test.status === 'draft' ? (
          <button
            onClick={() => onStatusChange(test.id, test.status === 'draft' ? 'start' : 'resume')}
            className='flex-1 flex items-center justify-center gap-1.5 py-2.5 text-zinc-400 hover:text-emerald-500 font-mono text-[10px] uppercase tracking-wider transition-colors'
          >
            <Play className='w-3.5 h-3.5' /> {test.status === 'draft' ? t('ab_testing.start') : t('ab_testing.resume')}
          </button>
        ) : null}
        <button
          onClick={() => onDelete(test)}
          className='flex-1 flex items-center justify-center gap-1.5 py-2.5 text-zinc-400 hover:text-red-400 font-mono text-[10px] uppercase tracking-wider transition-colors'
        >
          <Trash2 className='w-3.5 h-3.5' /> {t('ab_testing.delete')}
        </button>
      </div>
    </div>
  );
}

// ─── Template Card ───
function TemplateCard({ template, onUse }) {
  const { t } = useTranslation();
  const TypeIcon = typeIcons[template.type];

  return (
    <div className='border border-zinc-800 bg-zinc-950/30 hover:border-zinc-700 transition-colors p-5'>
      <div className='flex items-start gap-3 mb-3'>
        <div className='p-2 border border-zinc-700 bg-zinc-900'>
          <TypeIcon className='w-5 h-5 text-amber-500' />
        </div>
        <div className='flex-1'>
          <h3 className='font-display text-sm uppercase'>{template.name}</h3>
          <p className='text-zinc-500 text-xs mt-1'>{template.description}</p>
        </div>
      </div>
      <div className='flex items-center gap-3 mb-4 text-zinc-500'>
        <span className='font-mono text-[9px] uppercase tracking-wider'>
          {successMetrics.find(m => m.value === template.recommendedMetric)?.label}
        </span>
        <span className='font-mono text-[9px]'>·</span>
        <span className='font-mono text-[9px] uppercase tracking-wider'>
          {template.recommendedDuration} {t('ab_testing.days')}
        </span>
        <span className='font-mono text-[9px]'>·</span>
        <span className='font-mono text-[9px] uppercase tracking-wider'>
          {template.recommendedSplit.a}/{template.recommendedSplit.b}
        </span>
      </div>
      <button
        onClick={() => onUse(template)}
        className='w-full flex items-center justify-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors'
      >
        <Zap className='w-3.5 h-3.5' /> {t('ab_testing.use_template')}
      </button>
    </div>
  );
}

// ─── Notification Integration ───
const AB_NOTIF_KEY = 'maxemployee.abNotifications';

function loadAbNotifications() {
  try {
    const stored = localStorage.getItem(AB_NOTIF_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

function saveAbNotifications(notifs) {
  try {
    localStorage.setItem(AB_NOTIF_KEY, JSON.stringify(notifs));
  } catch {}
}

function addAbNotification(notif) {
  const notifs = loadAbNotifications();
  notifs.unshift({
    id: `ab-notif-${Date.now()}`,
    type: 'ab_test',
    read: false,
    createdAt: new Date().toISOString(),
    ...notif,
  });
  // Keep max 20
  if (notifs.length > 20) notifs.pop();
  saveAbNotifications(notifs);
  return notifs;
}

// ─── Main ABTestManager ───
export default memo(function ABTestManager() {
  const { t } = useTranslation();
  const [tests, setTests] = useState(() => getEnrichedTests());
  const [viewingTest, setViewingTest] = useState(null);
  const [editingTest, setEditingTest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('experiments'); // experiments, templates, archive

  const refresh = useCallback(() => {
    setTests(getEnrichedTests());
  }, []);

  const filteredTests = useMemo(() => {
    return tests.filter(t => {
      if (activeTab === 'archive') {
        if (t.status !== 'archived') return false;
      } else {
        if (t.status === 'archived') return false;
      }
      if (filterStatus !== 'all' && t.status !== filterStatus) return false;
      if (filterType !== 'all' && t.type !== filterType) return false;
      if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [tests, filterStatus, filterType, searchQuery, activeTab]);

  const handleStatusChange = useCallback((testId, action) => {
    let updatedTest;
    switch (action) {
      case 'start':
        updatedTest = startTest(testId);
        addAbNotification({
          title: t('ab_testing.notif_test_started'),
          message: tests.find(t => t.id === testId)?.name || '',
        });
        break;
      case 'pause':
        updatedTest = pauseTest(testId);
        addAbNotification({
          title: t('ab_testing.notif_test_paused'),
          message: tests.find(t => t.id === testId)?.name || '',
        });
        break;
      case 'resume':
        updatedTest = resumeTest(testId);
        break;
      case 'complete':
        updatedTest = completeTest(testId);
        addAbNotification({
          title: t('ab_testing.notif_test_completed'),
          message: tests.find(t => t.id === testId)?.name || '',
        });
        break;
      case 'archive':
        updatedTest = archiveTest(testId);
        break;
      default:
        return;
    }
    refresh();
  }, [refresh, tests, t]);

  const handleDelete = useCallback((test) => {
    if (confirm(t('ab_testing.confirm_delete', { name: test.name }))) {
      deleteTest(test.id);
      refresh();
    }
  }, [refresh, t]);

  const handleSave = useCallback((testData) => {
    if (editingTest) {
      updateTest(editingTest.id, testData);
    } else {
      createTest(testData);
    }
    setShowCreateModal(false);
    setEditingTest(null);
    refresh();
  }, [editingTest, refresh]);

  const handleUseTemplate = useCallback((template) => {
    const testData = {
      name: template.name,
      type: template.type,
      description: template.description,
      hypothesis: template.hypothesis || '',
      variantA: template.variantA,
      variantB: template.variantB,
      splitRatio: template.recommendedSplit,
      successMetric: template.recommendedMetric,
      estimatedDurationDays: template.recommendedDuration,
      audiencePercentage: 100,
      audienceSegments: [],
    };
    createTest(testData);
    addAbNotification({
      title: t('ab_testing.notif_test_created'),
      message: template.name,
    });
    refresh();
    setActiveTab('experiments');
  }, [refresh, t]);

  // Detail view
  if (viewingTest) {
    const test = tests.find(t => t.id === viewingTest.id) || viewingTest;
    return (
      <TestDetail
        test={test}
        onBack={() => { setViewingTest(null); refresh(); }}
        onStatusChange={handleStatusChange}
      />
    );
  }

  const runningCount = tests.filter(t => t.status === 'running').length;
  const completedCount = tests.filter(t => t.status === 'completed').length;
  const draftCount = tests.filter(t => t.status === 'draft').length;
  const totalImpressions = tests.reduce((acc, t) => acc + t.stats.totalImpressions, 0);

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
        <div>
          <h2 className='font-display text-2xl uppercase'>{t('ab_testing.title')}</h2>
          <p className='text-zinc-500 text-sm mt-1'>{t('ab_testing.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className='flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors shrink-0'
        >
          <Plus className='w-4 h-4' /> {t('ab_testing.new_experiment')}
        </button>
      </div>

      {/* Summary Stats */}
      <div className='grid grid-cols-2 md:grid-cols-5 gap-4'>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.total_tests')}</div>
          <div className='font-display text-2xl'>{tests.filter(t => t.status !== 'archived').length}</div>
        </div>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.running')}</div>
          <div className='font-display text-2xl text-emerald-400'>{runningCount}</div>
        </div>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.completed')}</div>
          <div className='font-display text-2xl text-sky-400'>{completedCount}</div>
        </div>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.draft')}</div>
          <div className='font-display text-2xl text-zinc-400'>{draftCount}</div>
        </div>
        <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
          <div className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2'>{t('ab_testing.total_impressions')}</div>
          <div className='font-display text-2xl'>{formatNumber(totalImpressions)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className='flex items-center gap-1 border-b border-zinc-800'>
        <button
          onClick={() => setActiveTab('experiments')}
          className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border-b-2 transition-colors ${
            activeTab === 'experiments'
              ? 'border-amber-500 text-amber-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <Activity className='w-3.5 h-3.5 inline mr-1.5' />
          {t('ab_testing.experiments')}
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border-b-2 transition-colors ${
            activeTab === 'templates'
              ? 'border-amber-500 text-amber-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <FileText className='w-3.5 h-3.5 inline mr-1.5' />
          {t('ab_testing.templates')}
        </button>
        <button
          onClick={() => setActiveTab('archive')}
          className={`px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border-b-2 transition-colors ${
            activeTab === 'archive'
              ? 'border-amber-500 text-amber-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <Archive className='w-3.5 h-3.5 inline mr-1.5' />
          {t('ab_testing.archive')}
        </button>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className='space-y-4'>
          <p className='text-zinc-500 text-sm'>{t('ab_testing.templates_description')}</p>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {testTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                onUse={handleUseTemplate}
              />
            ))}
          </div>
        </div>
      )}

      {/* Experiments / Archive Tab */}
      {activeTab !== 'templates' && (
        <>
          {/* Filters */}
          <div className='flex flex-col md:flex-row gap-3'>
            <div className='flex-1'>
              <input
                type='text'
                placeholder={t('ab_testing.search_placeholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className='w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-3 py-2 font-mono text-xs outline-none transition-colors'
              />
            </div>
            <div className='flex gap-2'>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className='bg-zinc-950 border border-zinc-800 text-zinc-300 px-3 py-2 font-mono text-xs outline-none'
              >
                <option value='all'>{t('ab_testing.all_status')}</option>
                {statusOptions.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className='bg-zinc-950 border border-zinc-800 text-zinc-300 px-3 py-2 font-mono text-xs outline-none'
              >
                <option value='all'>{t('ab_testing.all_types')}</option>
                {testTypes.map(tp => (
                  <option key={tp.value} value={tp.value}>{tp.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Test List */}
          {filteredTests.length === 0 ? (
            <div className='border border-zinc-800 p-12 text-center'>
              <BarChart3 className='w-10 h-10 text-zinc-700 mx-auto mb-4' />
              <div className='font-display text-xl uppercase mb-2'>
                {activeTab === 'archive' ? t('ab_testing.no_archived') : t('ab_testing.no_tests')}
              </div>
              <p className='text-zinc-500 max-w-md mx-auto text-sm mb-6'>
                {activeTab === 'archive'
                  ? t('ab_testing.no_archived_desc')
                  : tests.filter(t => t.status !== 'archived').length === 0
                  ? t('ab_testing.create_first_desc')
                  : t('ab_testing.no_filter_results')
                }
              </p>
              {activeTab !== 'archive' && tests.filter(t => t.status !== 'archived').length === 0 && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className='inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors'
                >
                  <Plus className='w-3.5 h-3.5' /> {t('ab_testing.create_first_test')}
                </button>
              )}
            </div>
          ) : (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              {filteredTests.map(test => (
                <TestCard
                  key={test.id}
                  test={test}
                  onView={setViewingTest}
                  onEdit={setEditingTest}
                  onStatusChange={handleStatusChange}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {(showCreateModal || editingTest) && (
        <ExperimentForm
          test={editingTest}
          onSave={handleSave}
          onClose={() => { setShowCreateModal(false); setEditingTest(null); }}
        />
      )}
    </div>
  );
});
